/**
 * Accept/dismiss false positives (cue-forge-n3e.7). Real subtitles carry
 * intentional over-spec cues — a forced over-CPS credit, a deliberate one-line
 * lyric. Marking such an issue "accepted" subtracts it from the counts and the
 * review loop so the operator never has to re-skip it on the next QC pass.
 *
 * The accept identity is a CONTENT hash, deliberately index-free. The bead text
 * said "ruleId + cueIndex + content hash", but including cueIndex defeats its own
 * stated goal — renumbering (an empty-cue delete + reindex, a merge/split) shifts
 * cueIndex while the cue's text is unchanged. Hashing the cue's TEXT instead
 * survives renumber/shift/scale and correctly LAPSES on a real text rewrite (an
 * edit to the dialogue should re-surface the issue for re-judgment). cueIndex is
 * used only at runtime to locate the cue whose text we hash.
 *
 * Pure and framework-free so it is unit-testable and stays off any provider file.
 */

import type { QCIssue, SubtitleDocument } from '../../core';
import { cueToText } from '../../core';

/**
 * cyrb53 — a tiny, dependency-free, well-distributed 53-bit string hash (NOT a
 * cryptographic hash; we only need collision-resistance across the handful of
 * accepts in one file). Its output is a PERSISTENCE CONTRACT: it is serialized
 * into `LoadedDocument.qcAccepted` and must never change, or every stored accept
 * would silently stop matching on the next deploy. A snapshot test pins it.
 */
function cyrb53(str: string, seed = 0): number {
  let h1 = 0xdeadbeef ^ seed;
  let h2 = 0x41c6ce57 ^ seed;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761);
    h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return 4294967296 * (2097151 & h2) + (h1 >>> 0);
}

/** Stable, compact base36 digest of a string. See {@link cyrb53}. */
export function hashContent(s: string): string {
  return cyrb53(s).toString(36);
}

/**
 * The persisted identity of an accepted issue: `ruleId:<hash(cueText)>` for a
 * cue issue, or `ruleId:doc:<hash(message)>` for a document-level issue (or one
 * whose cue cannot be resolved). Index-free, so it survives renumbering.
 *
 * Hashes the cue TEXT, deliberately NOT the issue message — so an accept survives
 * a benign timing change (a Sync shift/scale/drift moves the cps/duration value
 * embedded in the message, but the cue's words are unchanged). The trade-off is
 * COARSENING: every issue a rule raises for one cue shares this key, so two
 * over-long lines of the same cue (max-chars-per-line emits one issue per line),
 * or two byte-identical cues flagged by the same rule, accept and restore as a
 * unit. That is the intended granularity — "this cue's <rule> is intentional" —
 * and keeping the measured value OUT of the key is what makes accepts persist
 * across the re-runs the feature exists to survive (cue-forge-n3e.7).
 */
export function acceptKey(issue: QCIssue, doc: SubtitleDocument | null): string {
  if (issue.cueIndex >= 0 && doc) {
    const cue = doc.cues.find((c) => c.index === issue.cueIndex);
    if (cue) return `${issue.ruleId}:${hashContent(cueToText(cue))}`;
  }
  return `${issue.ruleId}:doc:${hashContent(issue.message)}`;
}

/**
 * Split a freshly-evaluated issue list into the ACTIVE issues (which drive the
 * counts, the cursor, and the grid markers) and the ACCEPTED subset (the
 * collapsed "Accepted (n)" group). Order is preserved within each bucket.
 * Accepted keys that match no live issue are simply unused (dormant orphans).
 */
export function partitionAccepted(
  found: QCIssue[],
  acceptedKeys: Set<string>,
  doc: SubtitleDocument | null,
): { active: QCIssue[]; accepted: QCIssue[] } {
  if (acceptedKeys.size === 0) return { active: found, accepted: [] };
  const active: QCIssue[] = [];
  const accepted: QCIssue[] = [];
  for (const issue of found) {
    (acceptedKeys.has(acceptKey(issue, doc)) ? accepted : active).push(issue);
  }
  return { active, accepted };
}
